import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const TerminalContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '400px',
  backgroundColor: 'rgba(11, 8, 19, 0.95)',
  border: '1px solid #39FF14',
  borderRadius: '5px',
  padding: '15px',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '12px',
  boxShadow: '0 0 20px rgba(57, 255, 20, 0.2)',
  overflowY: 'auto',
  maxHeight: '300px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '12px',
    fontSize: '11px',
    maxHeight: '250px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    fontSize: '10px',
    maxHeight: '200px',
  },
}));

const TerminalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
  paddingBottom: '10px',
  borderBottom: '1px solid #39FF14',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '8px',
    paddingBottom: '8px',
    gap: '8px',
  },
}));

const TerminalDot = styled(Box)(({ theme }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#39FF14',
  boxShadow: '0 0 10px #39FF14',
}));

const TerminalTitle = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontSize: '11px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const TerminalOutput = styled(Box)(({ theme }) => ({
  color: '#39FF14',
  lineHeight: '1.6',
  minHeight: '100px',
  [theme.breakpoints.down('sm')]: {
    minHeight: '80px',
  },
}));

const TerminalCursor = styled('span')(({ theme }) => ({
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    '0%, 100%': { opacity: '1' },
    '50%': { opacity: '0' },
  },
}));

const TerminalConsole: React.FC = () => {
  const [output, setOutput] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  const bootSequence = [
    '> boot creative_engine',
    'loading visual memory...',
    'style map detected',
    'PIXELS CARRY DATA...SECRETS STAY HIDE...',
  ];

  useEffect(() => {
    let lineIndex = 0;
    let charIndex = 0;
    let currentOutput = '';

    const typeWriter = () => {
      if (lineIndex < bootSequence.length) {
        const currentLine = bootSequence[lineIndex];
        
        if (charIndex < currentLine.length) {
          currentOutput += currentLine[charIndex];
          charIndex++;
          setOutput(currentOutput);
          setTimeout(typeWriter, 30 + Math.random() * 50);
        } else {
          currentOutput += '\n';
          charIndex = 0;
          lineIndex++;
          setOutput(currentOutput);
          setTimeout(typeWriter, 200);
        }
      } else {
        // Blink cursor at the end
        setShowCursor(true);
      }
    };

    // Start typing after a delay
    const startDelay = setTimeout(typeWriter, 1000);
    return () => clearTimeout(startDelay);
  }, []);

  return (
    <TerminalContainer>
      <TerminalHeader>
        <TerminalDot />
        <TerminalTitle>CODE CONSOLE</TerminalTitle>
      </TerminalHeader>
      <TerminalOutput>
        {output}
        {showCursor && <TerminalCursor>_</TerminalCursor>}
      </TerminalOutput>
    </TerminalContainer>
  );
};

export default TerminalConsole;
